import type { UsageKindRow, UsageModelRow, UsageMonth, UsageProjectRow, UsageReport } from '@/api';

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}

function barTone(i: number): string {
  const tones = ['bg-accent', 'bg-success', 'bg-warning', 'bg-border'];
  return tones[i % tones.length];
}

function Rows({
  rows,
  total,
}: {
  rows: { key: string; label: string; sub?: string; tokens: number }[];
  total: number;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-texts">No token usage yet.</p>;
  }
  return (
    <div className="space-y-4">
      {rows.map((u, i) => {
        const pct = total === 0 ? 0 : Math.round((u.tokens / total) * 100);
        return (
          <div key={u.key}>
            <div className="flex items-center justify-between mb-1.5 gap-2">
              <span className="min-w-0">
                <span className="block text-sm text-texts truncate">{u.label}</span>
                {u.sub ? <span className="block text-xs text-texts/80 truncate">{u.sub}</span> : null}
              </span>
              <span className="shrink-0 text-sm font-mono text-textp">
                {u.tokens.toLocaleString()}
                {total ? ` (${pct}%)` : ''}
              </span>
            </div>
            <div className="h-2 rounded-full bg-surface2 overflow-hidden">
              <div className={`h-full rounded-full ${barTone(i)}`} style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TokenUsageView({ report }: { report: UsageReport }) {
  const max = Math.max(1, ...report.months.map((m) => m.tokens));
  const models: UsageModelRow[] = report.by_model || [];
  const projects: UsageProjectRow[] = report.by_project || [];
  const kinds: UsageKindRow[] = report.by_kind || [];
  const months: UsageMonth[] = report.months || [];

  return (
    <div data-testid="token-usage">
      <h1 className="text-xl font-semibold text-textp">Token usage</h1>
      <p className="text-sm text-texts mt-1">
        Prompt and completion tokens by project, test, and model. Local schema tests stay free.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-surface border border-border rounded-xl p-5">
          <p className="text-xs uppercase tracking-widest text-texts">This month</p>
          <p className="mt-2 text-3xl font-mono text-textp">{report.this_month_tokens.toLocaleString()}</p>
          <p className="mt-1 text-xs text-texts">tokens</p>
        </div>
        <div className="bg-surface border border-border rounded-xl p-5">
          <p className="text-xs uppercase tracking-widest text-texts">Lifetime</p>
          <p className="mt-2 text-3xl font-mono text-textp">{report.total_tokens.toLocaleString()}</p>
          <p className="mt-1 text-xs text-texts">
            {report.prompt_tokens.toLocaleString()} prompt · {report.completion_tokens.toLocaleString()} completion
          </p>
        </div>
        <div className="bg-surface border border-border rounded-xl p-5">
          <p className="text-xs uppercase tracking-widest text-texts">Models</p>
          <p className="mt-2 text-3xl font-mono text-textp">{models.length}</p>
          <p className="mt-1 text-xs text-texts">distinct models used</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-textp">Last 6 months</h2>
            <span className="text-xs text-texts">tokens / month</span>
          </div>

          <div className="mt-6 flex items-end justify-between gap-2 h-48">
            {months.map((m) => {
              const height = Math.max(8, Math.round((m.tokens / max) * 100));
              return (
                <div key={m.key} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-xs font-mono text-texts">{formatTokens(m.tokens)}</span>
                  <div className="w-full max-w-12 bg-accentSoft rounded-t-lg flex items-end justify-center h-full">
                    <div className="w-full bg-accent rounded-t-lg" style={{ height: `${height}%` }} />
                  </div>
                  <span className="text-xs text-texts">{m.month}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-surface border border-border rounded-xl p-5">
          <h2 className="text-base font-semibold text-textp">By activity</h2>
          <div className="mt-4">
            <Rows
              total={report.total_tokens}
              rows={kinds.map((k) => ({ key: k.kind, label: k.label, tokens: k.tokens }))}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-surface border border-border rounded-xl p-5">
          <h2 className="text-base font-semibold text-textp">By model</h2>
          <div className="mt-4">
            <Rows
              total={report.total_tokens}
              rows={models.map((m) => ({
                key: `${m.provider}/${m.model}`,
                label: m.model || 'unknown',
                sub: m.provider,
                tokens: m.tokens,
              }))}
            />
          </div>
        </div>

        <div className="bg-surface border border-border rounded-xl p-5">
          <h2 className="text-base font-semibold text-textp">By project</h2>
          <div className="mt-4 space-y-5">
            {projects.length === 0 ? (
              <p className="text-sm text-texts">No project usage yet.</p>
            ) : (
              projects.map((p) => (
                <div key={p.project_id || p.project_title} data-testid="usage-project">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <span className="text-sm font-medium text-textp truncate">{p.project_title}</span>
                    <span className="shrink-0 text-sm font-mono text-textp">{p.tokens.toLocaleString()}</span>
                  </div>
                  {(p.by_model || []).map((m) => (
                    <div key={`${m.provider}/${m.model}`} className="flex items-center justify-between text-xs text-texts py-0.5">
                      <span className="truncate">
                        {m.model}
                        {m.provider ? ` · ${m.provider}` : ''}
                      </span>
                      <span className="font-mono">{m.tokens.toLocaleString()}</span>
                    </div>
                  ))}
                  {(p.by_kind || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {p.by_kind.map((k) => (
                        <span
                          key={k.kind}
                          className="text-xs px-2 py-0.5 rounded-full bg-surface2 text-texts border border-border"
                        >
                          {k.label} {k.tokens.toLocaleString()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
